import { EditOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Divider, Flex, message, Table, TableColumnsType } from 'antd'
import { MenuProps } from 'antd/lib'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  useTemplatesServiceDeleteSystemTemplateEndpoint,
  useTemplatesServiceGetSystemTemplateListEndpoint,
  useTemplatesServiceGetSystemTemplateListEndpointKey
} from 'src/api/queries'
import { ApiError, ContentType, Permissions } from 'src/api/requests'
import {
  DeleteConfirm,
  PageBreadcrumbs,
  SearchBar,
  TableActionButtons
} from 'src/components'
import { useTablePagination, useUser } from 'src/hooks'
import { formErrorHandler, resetPagination } from 'src/utils'

import { TreatmentTemplateModal } from '../components'

export const TreatmentTemplatesPage = () => {
  const { t } = useTranslation()
  const { hasPermission } = useUser()
  const pagination = useTablePagination()
  const queryClient = useQueryClient()

  const [search, setSearch] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [templateId, setTemplateId] = useState<string>()

  const { userData } = useUser()

  const queryParams = {
    pageNumber: Number(pagination.current),
    pageSize: Number(pagination.pageSize),
    institutionIds: userData?.currentLegalEntityId
      ? [userData?.currentLegalEntityId]
      : [],
    type: [ContentType.CONSULTATION],
    ...(!!search && {
      searchTerm: search
    })
  }

  const { data, isLoading } =
    useTemplatesServiceGetSystemTemplateListEndpoint(queryParams)

  const { mutate } = useTemplatesServiceDeleteSystemTemplateEndpoint({
    onError: (error: ApiError) => formErrorHandler(error),
    onSuccess: () => {
      message.success(t('counterparts.template_deleted'))
      queryClient.invalidateQueries({
        queryKey: [useTemplatesServiceGetSystemTemplateListEndpointKey]
      })
    }
  })

  const onOpenModal = (templateId?: string) => {
    setTemplateId(templateId ?? undefined)
    setIsModalOpen(true)
  }

  const onCloseModal = () => {
    setTemplateId(undefined)
    setIsModalOpen(false)
  }

  const getActionItems = useMemo(
    () =>
      (templateId: string): MenuProps['items'] => {
        return [
          ...(hasPermission(Permissions.TEMPLATES_MENU_CREATE_TEMPLATES)
            ? [
                {
                  key: 'edit',
                  label: t('common.edit'),
                  icon: <EditOutlined />,
                  onClick: () => onOpenModal(templateId)
                }
              ]
            : []),
          {
            key: 'divider',
            type: 'divider'
          },
          ...(hasPermission(Permissions.TEMPLATES_MENU_DELETE_TEMPLATES)
            ? [
                {
                  key: 'delete',
                  label: (
                    <DeleteConfirm
                      title={t('common.delete')}
                      onConfirm={() => mutate({ id: templateId })}
                    />
                  ),
                  danger: true
                }
              ]
            : [])
        ]
      },
    [t]
  )

  const columns = useMemo(
    (): TableColumnsType => [
      {
        key: 'name',
        title: t('counterparts.template_name'),
        dataIndex: 'name'
      },
      {
        key: 'description',
        title: t('counterparts.description'),
        render: ({ description }) => description ?? '-'
      },
      {
        width: 100,
        key: 'actions',
        title: t('common.action'),
        align: 'center',
        render: ({ id: templateId }) => (
          <TableActionButtons
            items={getActionItems(templateId)}
            justify="center"
          />
        )
      }
    ],
    [t]
  )

  const onSearch = (val: string) => {
    setSearch(val)
    resetPagination(pagination)
  }

  return (
    <PageBreadcrumbs
      title={t('menu.treatments')}
      items={[
        {
          title: t('menu.templates')
        },
        {
          title: t('menu.treatments')
        }
      ]}
    >
      <Flex gap={16} justify="space-between">
        <SearchBar
          onSearch={onSearch}
          placeholder={t('reports.search_by_report_title')}
        />

        <Button type="primary" onClick={() => onOpenModal()}>
          {t('templates.create_treatment_template')}
        </Button>
      </Flex>

      <Divider />

      <Table
        loading={isLoading}
        rowKey="id"
        bordered
        dataSource={data?.data}
        columns={columns}
        scroll={{ x: 'max-content' }}
        pagination={{ ...pagination, total: data?.totalCount }}
      />

      <TreatmentTemplateModal
        open={isModalOpen}
        templateId={templateId}
        onCloseModal={onCloseModal}
      />
    </PageBreadcrumbs>
  )
}
